import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from '../../language/en.json';
import hu from '../../language/hu.json';
import jp from '../../language/jp.json';
import haw from '../../language/haw.json';

const LANG_LIST = ['en', 'hu', 'jp', 'haw'];

// Origin of the fireknife-i18n tool that embeds this app in an iframe for live
// translation editing. postMessage language-change events are only honored from
// a trusted origin, and language files are only fetched from it. In dev mode we
// also accept localhost / 127.0.0.1 so the locally-served i18n tool can drive
// the embedded admin.
const I18N_TOOL_ORIGIN = 'https://fireknife18n.donebox.hu';

function isTrustedI18nOrigin(origin: string): boolean {
    if (origin === I18N_TOOL_ORIGIN) return true;
    if (!import.meta.env.DEV) return false;
    try {
        const host = new URL(origin).hostname;
        return host === 'localhost' || host === '127.0.0.1';
    } catch {
        return false;
    }
}

i18n.use(initReactI18next).init({
    resources: {
        en: {
            translation: en,
        },
        jp: {
            translation: jp,
        },
        hu: {
            translation: hu,
        },
        haw: {
            translation: haw,
        },
    },
    // Hawaiian ships as an empty bundle for now; i18next falls back to English
    // until the translations are filled in via the fireknife-i18n tool.
    fallbackLng: 'en',
    lng: 'en', // default language to use.
    compatibilityJSON: 'v3',
});

export function getLanguages(): string[] {
    return LANG_LIST;
}

export function changeLanguage(language: string): void {
    i18n.changeLanguage(language);
}

if (typeof window !== 'undefined') {
    window.addEventListener('message', async (event: MessageEvent) => {
        if (event.data?.type !== 'FIRESKNIFE_I18N_LANG_CHANGE') return;
        // Only trust messages from the i18n tool that embeds us.
        if (event.source !== window.parent || !isTrustedI18nOrigin(event.origin)) return;
        const { language, languageFile, languageData } = event.data as {
            language?: string;
            languageFile?: string;
            languageData?: Record<string, unknown>;
        };
        if (!language) return;
        try {
            let data = languageData;
            if (!data && languageFile) {
                // Resolve against the sender's origin and reject anything that
                // points elsewhere — never fetch a translation bundle off-origin.
                const fileUrl = new URL(languageFile, event.origin);
                if (fileUrl.origin !== event.origin) return;
                const res = await fetch(fileUrl);
                const json = await res.json();
                data = json.data;
            }
            if (!data) return;
            i18n.addResourceBundle(language, 'translation', data, true, true);
            await i18n.changeLanguage(language);
        } catch (err) {
            console.error('Failed to apply i18n language change from iframe', err);
        }
    });
}

export const i18nConfig = i18n;
